// externals
import type { Reducer } from 'redux';
import {
    HIDE_EDIT_CUSTOMER_SLIDEOUT,
    SHOW_EDIT_CUSTOMER_SLIDEOUT,
    SET_CUSTOMER_TO_SYNC,
    SYNC_CUSTOMER_REQUEST,
    SYNC_CUSTOMER_SUCCESS,
    SYNC_CUSTOMER_FAILURE,
    REMOVE_CUSTOMER_FROM_SYNC,
    CREATE_BUSINESS_REQUEST,
    CREATE_BUSINESS_SUCCESS,
    CREATE_BUSINESS_FAILURE,
    CLEAR_CREATE_BUSINESS_ERROR,
    SHOW_BUSINESS_SEARCH,
    HIDE_BUSINESS_SEARCH,
    BUSINESS_SEARCH_REQUEST,
    BUSINESS_SEARCH_SUCCESS,
    BUSINESS_SEARCH_FAILURE,
    CLEAR_BUSINESS_SEARCH_RESULTS,
    SELECT_BUSINESS_RESULT,
    ASSOCIATE_BUSINESS_REQUEST,
    ASSOCIATE_BUSINESS_SUCCESS,
    ASSOCIATE_BUSINESS_FAILURE,
    CLEAR_ASSOCIATE_BUSINESS_ERROR
} from '../actionTypes/editCustomerActionTypes';

export interface BusinessEntityAddress {
    street1?: string;
    street2?: string;
    city?: string;
    stateOrProvince?: string;
    postalCode?: string;
    countyOrRegion?: string;
    country?: string;
    stateOrProvinceAbbreviation?: string;
    countryAbbreviation?: string;
}

export interface BusinessEntity {
    businessEntityId: string;
    businessName: string;
    primaryAddress?: BusinessEntityAddress;
    billingAddress?: BusinessEntityAddress;
    primaryPhone?: string;
    primaryEmail?: string;
}

type editCustomerSlideout = {
    showEditCustomer: string;
    customersToSync: string[];
    syncCustomerLoading: boolean;
    syncCustomerErrors: any[];
    createBusinessLoading: boolean;
    createBusinessError: string;
    businessSearch: {
        show: boolean;
        query: string;
        results: BusinessEntity[];
        loading: boolean;
        error: string | null;
        selectedBusinessEntityId: string | null;
        associateLoading: boolean;
        associateError: string | null;
    };
};

export const EDIT_CUSTOMER_SLIDEOOUT_INITIAL_STATE = {
    showEditCustomer: '',
    customersToSync: [],
    syncCustomerLoading: false,
    syncCustomerErrors: [],
    createBusinessLoading: false,
    createBusinessError: '',
    businessSearch: {
        show: false,
        query: '',
        results: [] as BusinessEntity[],
        loading: false,
        error: null as string | null,
        selectedBusinessEntityId: null as string | null,
        associateLoading: false,
        associateError: null as string | null
    }
};

export type editCustomerReducer = Reducer<editCustomerSlideout>;

export const reducer: Reducer<editCustomerSlideout> = (
    state: editCustomerSlideout = EDIT_CUSTOMER_SLIDEOOUT_INITIAL_STATE,
    action: any
) => {
    switch (action.type) {
        case SHOW_EDIT_CUSTOMER_SLIDEOUT: {
            return {
                ...state,
                showEditCustomer: action.payload.showEditCustomer
            };
        }

        case HIDE_EDIT_CUSTOMER_SLIDEOUT: {
            return {
                ...state,
                showEditCustomer: '',
                createBusinessError: ''
            };
        }

        case SET_CUSTOMER_TO_SYNC: {
            const commonConsumerId: string = action.payload;
            return {
                ...state,
                customersToSync: [...state.customersToSync, commonConsumerId]
            };
        }
        case REMOVE_CUSTOMER_FROM_SYNC: {
            const commonConsumerId: string = action.payload;
            return {
                ...state,
                customersToSync: state.customersToSync.filter((id) => id !== commonConsumerId)
            };
        }
        case SYNC_CUSTOMER_REQUEST: {
            return {
                ...state,
                syncCustomerLoading: true
            };
        }
        case SYNC_CUSTOMER_SUCCESS: {
            return {
                ...state,
                customersToSync: [],
                syncCustomerLoading: false
            };
        }

        case SYNC_CUSTOMER_FAILURE: {
            return {
                ...state,
                syncCustomerLoading: false,
                syncCustomerErrors: action.payload
            };
        }

        case CREATE_BUSINESS_REQUEST: {
            return {
                ...state,
                createBusinessLoading: true,
                createBusinessError: ''
            };
        }

        case CREATE_BUSINESS_SUCCESS: {
            return {
                ...state,
                createBusinessLoading: false,
                createBusinessError: ''
            };
        }

        case CREATE_BUSINESS_FAILURE: {
            return {
                ...state,
                createBusinessLoading: false,
                createBusinessError: action.payload || 'CreateBusinessFailed'
            };
        }

        case CLEAR_CREATE_BUSINESS_ERROR: {
            return {
                ...state,
                createBusinessError: ''
            };
        }

        case SHOW_BUSINESS_SEARCH: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    show: true
                }
            };
        }

        case HIDE_BUSINESS_SEARCH: {
            return {
                ...state,
                businessSearch: {
                    ...EDIT_CUSTOMER_SLIDEOOUT_INITIAL_STATE.businessSearch
                }
            };
        }

        case BUSINESS_SEARCH_REQUEST: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    query: action.payload.query,
                    loading: true,
                    error: null
                }
            };
        }

        case BUSINESS_SEARCH_SUCCESS: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    results: action.payload.results,
                    loading: false,
                    error: null
                }
            };
        }

        case BUSINESS_SEARCH_FAILURE: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    results: [],
                    loading: false,
                    error: action.payload.error
                }
            };
        }

        case CLEAR_BUSINESS_SEARCH_RESULTS: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    query: '',
                    results: [],
                    error: null
                }
            };
        }

        case SELECT_BUSINESS_RESULT: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    selectedBusinessEntityId: action.payload.businessEntityId
                }
            };
        }

        case ASSOCIATE_BUSINESS_REQUEST: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    associateLoading: true,
                    associateError: null
                }
            };
        }

        case ASSOCIATE_BUSINESS_SUCCESS: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    associateLoading: false,
                    associateError: null
                }
            };
        }

        case ASSOCIATE_BUSINESS_FAILURE: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    associateLoading: false,
                    associateError: action.payload.error
                }
            };
        }

        case CLEAR_ASSOCIATE_BUSINESS_ERROR: {
            return {
                ...state,
                businessSearch: {
                    ...state.businessSearch,
                    associateError: null
                }
            };
        }

        default:
            return state;
    }
};
